import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ArrowUpRight, Menu, X } from "lucide-react";

import Certificates from "@/pages/LandingPage/Certificates";
import { setShowBanner } from "@/store/cookies/cookieConsentSlice";
import { CURRENT_YEAR, SOCIAL_LINKS } from "@/constants/landing";
import { scrollWindowToTop, useScrollToTop } from "@/hooks/useScrollToTop";
import logoText from "@/assets/images/unzippd-wordmark-white-v7lIpH0R.png";
import logoIcon from "@/assets/images/unzippd-logo.png";
import DemoModal from "@/components/Modals/DemoModal";
import { FAQ_SECTIONS, getFaqSectionElementId } from "@/constants/faqSections";

const MOBILE_DRAWER_NAV_ITEM =
  "mb-3 block w-full cursor-pointer border-0 bg-transparent px-0 py-0 text-left text-lg font-semibold text-white no-underline transition-colors hover:text-gray-300 active:text-white visited:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40";

const MOBILE_DRAWER_FAQ_SUBLINK =
  "block w-full py-1 pl-4 text-left text-sm text-gray-300 no-underline transition-colors hover:text-white active:text-white visited:text-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/40";

const PublicLayout = ({
  children,
  onFeaturesClick,
  onWhyUnzippdClick,
  onContactClick,
  onDemoClick,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const handleCookieSettingsClick = () => {
    scrollWindowToTop();
    dispatch(setShowBanner(true));
  };

  const closeDrawer = () => setIsDrawerOpen(false);

  const openDrawer = () => setIsDrawerOpen(true);

  const handleFeaturesClick = () => {
    if (onFeaturesClick) {
      onFeaturesClick();
      return;
    }
    navigate("/#features");
  };

  const handleWhyClick = () => {
    if (onWhyUnzippdClick) {
      onWhyUnzippdClick();
      return;
    }
    navigate("/#why-unzippd");
  };

  const handleContactClick = () => {
    if (onContactClick) {
      onContactClick();
      return;
    }
    navigate("/#contact-us");
  };

  const handleMobileFeaturesClick = () => {
    if (onFeaturesClick) {
      onFeaturesClick();
    } else {
      navigate("/#features");
    }
    closeDrawer();
  };

  const handleMobileWhyClick = () => {
    if (onWhyUnzippdClick) {
      onWhyUnzippdClick();
    } else {
      navigate("/#why-unzippd");
    }
    closeDrawer();
  };

  const handleMobileContactClick = () => {
    if (onContactClick) {
      onContactClick();
    } else {
      navigate("/#contact-us");
    }
    closeDrawer();
  };

  const handleDemoClick = () => {
    if (onDemoClick) {
      onDemoClick();
      return;
    }

    setIsDemoModalOpen(true);
  };

  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDrawerOpen]);

  useScrollToTop();

  return (
    <div className="min-h-screen bg-[#222222]">
      <header className="fixed w-full top-0 bg-black z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link
            to="/"
            aria-label="Unzippd home"
            className="w-2/5 lg:w-auto text-white text-2xl font-extrabold no-underline flex"
          >
            <img
              src={logoIcon}
              alt="Unzippd Word Mark"
              className="w-8 mr-4 inline-block"
            />
            <img
              src={logoText}
              alt="Unzippd Word Mark"
              className="w-[8rem] hidden lg:inline-block"
            />
          </Link>
          <div className="w-3/5 lg:w-auto flex items-center justify-end space-x-1.5 lg:space-x-4">
            <nav
              aria-label="Primary"
              className="hidden lg:flex items-center space-x-4"
            >
              <button
                type="button"
                onClick={handleFeaturesClick}
                className="border-0 bg-transparent text-base font-semibold text-white no-underline cursor-pointer hover:text-gray-300"
              >
                Features
              </button>
              <button
                type="button"
                onClick={handleWhyClick}
                className="border-0 bg-transparent text-base font-semibold text-white no-underline cursor-pointer hover:text-gray-300"
              >
                Why Unzippd
              </button>
              <div className="group relative">
                <button
                  type="button"
                  onClick={() => navigate("/faqs")}
                  className="border-0 bg-transparent text-base font-semibold text-white no-underline cursor-pointer hover:text-gray-300"
                >
                  FAQs
                </button>
                <div className="invisible absolute left-0 top-full z-50 mt-2 min-w-[200px] max-w-[280px] rounded-lg border border-gray-800 bg-[#111111] py-1 opacity-0 shadow-lg transition-all duration-150 group-hover:visible group-hover:opacity-100">
                  {FAQ_SECTIONS.map((title) => (
                    <Link
                      key={title}
                      to={`/faqs#${getFaqSectionElementId(title)}`}
                      className="block px-3 py-2 text-sm text-white no-underline hover:bg-[#1A1A1A]"
                    >
                      {title}
                    </Link>
                  ))}
                </div>
              </div>
              <Link
                to="/in-the-news"
                className="border-0 bg-transparent text-base font-semibold text-white no-underline cursor-pointer hover:text-gray-300"
              >
                In the news
              </Link>
              <button
                type="button"
                onClick={handleContactClick}
                className="inline-flex w-fit shrink-0 whitespace-nowrap border-0 bg-transparent p-0 text-base font-semibold text-white no-underline cursor-pointer hover:text-gray-300"
              >
                Contact us
              </button>
              <Link
                to="/auth/login"
                className="border-0 bg-transparent text-base font-semibold text-white no-underline cursor-pointer hover:text-gray-300"
              >
                Login
              </Link>
            </nav>
            <Button
              onClick={handleDemoClick}
              type="primary"
              className="bg-blue rounded-2xl border-none hover:bg-[#ff4a1a]"
            >
              Get a Demo <ArrowUpRight size={20} className="ml-0" />
            </Button>
            <button
              type="button"
              aria-label="Open menu"
              className="lg:hidden cursor-pointer hover:opacity-80 bg-transparent border-0 p-0"
              onClick={openDrawer}
            >
              <Menu color="#fff" size={24} />
            </button>
          </div>
          <div
            className={`test fixed inset-y-0 right-0 w-64 bg-[#111111] transform transition-transform duration-300 ease-in-out ${
              isDrawerOpen ? "translate-x-0" : "translate-x-full"
            } lg:hidden z-50`}
          >
            <div className="flex flex-col h-full">
              <div className="flex justify-end p-4">
                <button
                  type="button"
                  aria-label="Close menu"
                  className="cursor-pointer hover:opacity-80 bg-transparent border-0 p-0"
                  onClick={closeDrawer}
                >
                  <X size={24} color="#fff" />
                </button>
              </div>
              <nav
                aria-label="Mobile primary"
                className="flex flex-col p-4 pt-0 [-webkit-tap-highlight-color:transparent]"
              >
                <button
                  type="button"
                  onClick={handleMobileFeaturesClick}
                  className={MOBILE_DRAWER_NAV_ITEM}
                >
                  Features
                </button>
                <button
                  type="button"
                  onClick={handleMobileWhyClick}
                  className={MOBILE_DRAWER_NAV_ITEM}
                >
                  Why Unzippd
                </button>
                <div className="mb-3 flex flex-col py-0">
                  <Link
                    to="/faqs"
                    className={MOBILE_DRAWER_NAV_ITEM}
                    onClick={closeDrawer}
                  >
                    FAQs
                  </Link>
                  <div className="flex flex-col">
                    {FAQ_SECTIONS.map((title) => (
                      <Link
                        key={title}
                        to={`/faqs#${getFaqSectionElementId(title)}`}
                        className={MOBILE_DRAWER_FAQ_SUBLINK}
                        onClick={closeDrawer}
                      >
                        {title}
                      </Link>
                    ))}
                  </div>
                </div>
                <Link
                  to="/in-the-news"
                  className={MOBILE_DRAWER_NAV_ITEM}
                  onClick={closeDrawer}
                >
                  In the news
                </Link>
                <button
                  type="button"
                  onClick={handleMobileContactClick}
                  className={MOBILE_DRAWER_NAV_ITEM}
                >
                  Contact us
                </button>
                <Link
                  to="/auth/login"
                  className={MOBILE_DRAWER_NAV_ITEM}
                  onClick={closeDrawer}
                >
                  Login
                </Link>
              </nav>
            </div>
          </div>
          {isDrawerOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 lg:hidden"
              onClick={closeDrawer}
            />
          )}
        </div>
      </header>
      <main>{children}</main>
      <footer className="bg-[#222222] text-white py-6 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link
              to="/"
              aria-label="Unzippd home"
              className="w-auto text-white text-2xl font-extrabold no-underline flex items-center justify-center mb-4 md:mb-0"
            >
              <img
                src={logoIcon}
                alt="Unzippd Word Mark"
                className="w-8 mr-4 inline-block"
              />
              <img
                src={logoText}
                alt="Unzippd Word Mark"
                className="w-[8rem] inline-block"
              />
            </Link>
            <Certificates />
            <div className="flex gap-5 mb-4 mt-4">
              {SOCIAL_LINKS.map(({ href, icon: Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  aria-label={label}
                  title={label}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <Icon className="w-8 h-8 md:w-8 md:h-8 rounded-[6px]" />
                </a>
              ))}
            </div>
          </div>
          <div className="w-full md:w-auto text-center">
            <nav
              aria-label="Legal"
              className="flex flex-row flex-wrap items-center justify-center w-full gap-x-4 gap-y-2 text-sm mt-4"
            >
              <button
                type="button"
                onClick={handleCookieSettingsClick}
                className="text-white underline decoration-white cursor-pointer bg-transparent border-0 p-0 text-center"
              >
                Cookie Settings
              </button>
              <Link
                to="/terms-and-conditions"
                className="text-white underline decoration-white text-center"
              >
                Terms & Conditions
              </Link>
              <Link
                to="/privacy-policy"
                className="text-white underline decoration-white text-center"
              >
                Privacy Policy
              </Link>
            </nav>
            <div className="text-sm text-gray-400 w-full mt-2">
              © {CURRENT_YEAR} Unzippd. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
      <DemoModal
        isOpen={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
      />
    </div>
  );
};

export default PublicLayout;
